import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  QrCode, MessageCircle, Loader2, Link2, Info, Zap, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import ChannelBehaviorEditor from '@/components/chatbot/ChannelBehaviorEditor';

/**
 * WhatsApp Business channel card. Extracted from ChannelsTab.jsx during the
 * iter131 split — the parent file was ~1100 lines. Keeping the connect and
 * connected states co-located here means adding a new field (e.g. template
 * message ID) only touches this file.
 */
export const WhatsAppChannel = ({
  chatbotId,
  channels,
  setChannels,
  savingChannel,
  saveChannel,
  disconnectChannel,
  openQR,
}) => {
  const connected = !!channels.whatsapp?.connected;

  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-green-50/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#25D366] flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">WhatsApp Business</h4>
            <p className="text-sm text-slate-500">Auto-reply to customer messages</p>
          </div>
        </div>
        {connected && (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" /> Connected
          </Badge>
        )}
      </div>

      {!connected ? (
        <div className="space-y-3">
          <div className="rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <QrCode className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-900">Quick connect with your phone</p>
                <p className="text-xs text-emerald-700 mt-0.5">Scan a QR code with your phone camera — no tokens to copy.</p>
              </div>
              <Button
                size="sm"
                onClick={() => openQR('whatsapp', 'WhatsApp')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="whatsapp-qr-connect-btn"
              >
                <QrCode className="w-4 h-4 mr-1.5" /> Show QR
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span>or paste credentials manually</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-600 text-sm">Phone Number ID</Label>
              <Input
                value={channels.whatsapp?.phone || ''}
                onChange={(e) => setChannels(prev => ({ ...prev, whatsapp: { ...prev.whatsapp, phone: e.target.value } }))}
                placeholder="From Meta Business Suite"
                className="bg-white border-slate-300 text-slate-900 mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-600 text-sm">WhatsApp Business Account ID</Label>
              <Input
                value={channels.whatsapp?.business_id || ''}
                onChange={(e) => setChannels(prev => ({ ...prev, whatsapp: { ...prev.whatsapp, business_id: e.target.value } }))}
                placeholder="WABA ID"
                className="bg-white border-slate-300 text-slate-900 mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-600 text-sm">Access Token</Label>
            <Input
              type="password"
              value={channels.whatsapp?.api_key || ''}
              onChange={(e) => setChannels(prev => ({ ...prev, whatsapp: { ...prev.whatsapp, api_key: e.target.value } }))}
              placeholder="Permanent access token from Meta"
              className="bg-white border-slate-300 text-slate-900 mt-1"
            />
          </div>
          <div className="flex items-center justify-between pt-2">
            <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <Info className="w-4 h-4" /> Setup Guide
            </a>
            <Button
              onClick={() => saveChannel('whatsapp', channels.whatsapp)}
              disabled={savingChannel === 'whatsapp' || !channels.whatsapp?.phone}
              className="bg-[#25D366] hover:bg-[#128C7E]"
            >
              {savingChannel === 'whatsapp' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect WhatsApp
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-green-900">Connected to WhatsApp Business API</p>
              <p className="text-xs text-green-700">AI agent is actively responding to messages</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => disconnectChannel('whatsapp')} className="text-red-600 border-red-200 hover:bg-red-50">
              Disconnect
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 p-2 rounded-lg">
            <Zap className="w-4 h-4" />
            Active relay enabled - webhook endpoint configured
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              try {
                const r = await api.get(`/chatbots/${chatbotId}/channels/whatsapp/webhook-status`);
                const d = r.data;
                toast.message('WhatsApp Webhook Setup', {
                  description: (
                    `1. Go to Meta App Dashboard → WhatsApp → Configuration\n` +
                    `2. Callback URL: ${d.webhook_url_to_register}\n` +
                    `3. Verify Token: ${d.verify_token_to_register || '(set one in Admin → Social)'}\n` +
                    `4. Subscribe to "messages" field\n` +
                    `\nLast webhook received: ${d.last_webhook_received_at || 'never'}`
                  ),
                  duration: 18000,
                });
              } catch (e) { toast.error('Could not load diagnostics'); }
            }}
            data-testid="wa-diagnostics-btn"
          >
            Show webhook setup &amp; diagnostics
          </Button>
          <ChannelBehaviorEditor chatbotId={chatbotId} platform="whatsapp" label="WhatsApp" />
        </div>
      )}
    </div>
  );
};

export default WhatsAppChannel;
